import { type FormEvent, useState } from "react";
import { Link } from "react-router";

import { type QueryReply, ask, citationPlace, message } from "./queryClient";

function field(form: FormData, name: string): string {
  return String(form.get(name) ?? "").trim();
}

/** Ask one question of one class, for one engagement and optional run. */
export function KnowledgeQueryPage() {
  const [reply, setReply] = useState<QueryReply | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function onAsk(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const run = field(form, "run");
    setBusy(true);
    ask({
      engagement_id: field(form, "engagement"),
      question: field(form, "question"),
      question_class: field(form, "class"),
      ...(run ? { run_id: run } : {}),
    })
      .then((found) => {
        setReply(found);
        setError(null);
      })
      .catch((caught: unknown) => {
        setReply(null);
        setError(message(caught));
      })
      .finally(() => setBusy(false));
  }

  return (
    <section aria-labelledby="query-title">
      <h1 id="query-title">Knowledge query</h1>
      <p>Answers come only from qualified segments, and each one cites its source.</p>
      <form onSubmit={onAsk} aria-label="Ask a question">
        <label>
          Engagement id <input name="engagement" required />
        </label>
        <label>
          Run id <input name="run" />
        </label>
        <label>
          Question class <input name="class" required />
        </label>
        <label>
          Question <textarea name="question" required maxLength={2000} />
        </label>
        <button type="submit" disabled={busy}>
          Ask
        </button>
      </form>
      {error && <p role="alert">Could not ask: {error}</p>}
      {reply?.status === "answered" && (
        <>
          <h2>Answer</h2>
          <p>{reply.answer}</p>
          {reply.limitations.length > 0 && (
            <>
              <h3>Limitations</h3>
              <ul aria-label="Limitations">
                {reply.limitations.map((limit) => (
                  <li key={limit}>{limit}</li>
                ))}
              </ul>
            </>
          )}
          <h3>Citations</h3>
          <ul aria-label="Citations">
            {reply.citations.map((citation) => (
              <li key={citation.segment_id}>
                <Link to={`/knowledge-query/segments/${citation.segment_id}`}>
                  {citationPlace(citation)}
                </Link>{" "}
                ({citation.source_authority}
                {citation.document_revision ? `, revision ${citation.document_revision}` : ""})
              </li>
            ))}
          </ul>
        </>
      )}
      {reply?.status === "declined" && (
        <p role="status">Declined: {reply.decline_reason}</p>
      )}
      {reply?.status === "failed" && (
        <p role="alert">No answer: {reply.decline_reason}</p>
      )}
      {reply && Object.keys(reply.withheld).length > 0 && (
        <p>
          Segments withheld:{" "}
          {Object.entries(reply.withheld)
            .map(([reason, count]) => `${count} (${reason})`)
            .join(", ")}
          .
        </p>
      )}
      {reply && <p>Recorded as query {reply.query_id}, under retrieval policy version {reply.retrieval_policy_version}.</p>}
    </section>
  );
}
